import React, { useState, useEffect, useRef } from "react";
import {PlusCircle,MinusCircle,Pencil } from "lucide-react";
import {fetchMemberApi,FetchWorkAssignment,fetchWorkSchedule, saveWorkAssignmentsApi // Import the API function
} from "../../actioncreators/actioncreators";
import AddMember from "./AddMember";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import Dialog from "@mui/material/Dialog";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ReactComponent as UserImage } from "../../Assets/mobileUser.svg";
import { format } from "date-fns";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useSelector } from "react-redux";
import { useSuspension } from "../../contexts/SuspensionContext";

const borderAnimation = `
  @keyframes borderSlideOut {
    0% {
      border-image: linear-gradient(
        to right,
        transparent 0%,
        transparent 40%,
        #FF8800 40%,
        #FF8800 60%,
        transparent 60%,
        transparent 100%
      ) 1;
    }
    50% {
      border-image: linear-gradient(
        to right,
        transparent 0%,
        transparent 20%,
        #FF8800 20%,
        #FF8800 80%,
        transparent 80%,
        transparent 100%
      ) 1;
    }
    100% {
      border-image: linear-gradient(
        to right,
        #FF8800 0%,
        #FF8800 100%
      ) 1;
    }
  }
`;

// Add these custom styles at the top of the file, after the imports
const customDatePickerStyles = `
  .react-datepicker-wrapper {
    width: 100%;
  }
  
  .react-datepicker__input-container input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 11px;
  }

  .react-datepicker-popper {
    z-index: 500 !important;
  }

  .react-datepicker {
    font-size: 11px;
  }

  .react-datepicker__header {
    background-color: #f8f9fa;
  }

  .react-datepicker__day--selected {
    background-color: #FF8800;
  }

  .react-datepicker__day--disabled {
    color: #ccc;
  }
`;

const UserAvatar = ({ member }) => {
  const [imageError, setImageError] = useState(false);

  const getInitials = (fullName) => {
    return fullName ? fullName[0].toUpperCase() : "";
  };

  if (!member.user_image || imageError) {
    return (
      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
        {getInitials(member.full_name)}
      </div>
    );
  }

  return (
    <img
      src={`data:image/jpeg;base64,${member.user_image}`}
      alt={member.full_name}
      className="w-8 h-8 rounded-full object-cover"
      onError={() => setImageError(true)}
    />
  );
};

// Add these helper functions before the WorkAssignment component
const checkDateOverlap = (startDate, endDate, existingAssignments, currentIndex) => {
  return existingAssignments.some((assignment, index) => {
    if (index === currentIndex) return false; // Skip current assignment
    const assignmentStart = new Date(assignment.start_date);
    const assignmentEnd = new Date(assignment.end_date);
    
    return (
      (startDate >= assignmentStart && startDate <= assignmentEnd) ||
      (endDate >= assignmentStart && endDate <= assignmentEnd) ||
      (startDate <= assignmentStart && endDate >= assignmentEnd)
    );
  });
};

const getAvailableStartDate = (date, existingAssignments, currentIndex) => {
  const proposedDate = new Date(date);
  
  while (checkDateOverlap(proposedDate, proposedDate, existingAssignments, currentIndex)) {
    proposedDate.setDate(proposedDate.getDate() + 1);
  }
  
  return proposedDate;
};

const getAvailableEndDate = (startDate, existingAssignments, currentIndex) => {
  const proposedDate = new Date(startDate);
  
  while (checkDateOverlap(startDate, proposedDate, existingAssignments, currentIndex)) {
    proposedDate.setDate(proposedDate.getDate() + 1);
  }
  
  return proposedDate;
};

// Add this helper function before the WorkAssignment component
const isDateDisabled = (date, assignments, currentIndex) => {
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);

  return assignments.some((assignment, index) => {
    if (index === currentIndex) return false; // Skip current assignment
    if (!assignment.start_date || !assignment.end_date) return false;

    const startDate = new Date(assignment.start_date);
    const endDate = new Date(assignment.end_date);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    return checkDate >= startDate && checkDate <= endDate;
  });
};

// Add this new helper function near your other utility functions
const findNextAvailableDate = (currentEndDate, existingAssignments) => {
  let nextDate = new Date(currentEndDate);
  nextDate.setDate(nextDate.getDate() + 1); // Start with next day
  
  // Keep incrementing the date until we find one that doesn't overlap
  while (existingAssignments.some(assignment => {
    if (!assignment.start_date || !assignment.end_date) return false;
    const assignmentStart = new Date(assignment.start_date);
    const assignmentEnd = new Date(assignment.end_date);
    return nextDate >= assignmentStart && nextDate <= assignmentEnd;
  })) {
    nextDate.setDate(nextDate.getDate() + 1);
  }
  
  return nextDate;
};

const WorkAssignment = () => {
  // Suspension check
  const { isSuspended } = useSuspension();

  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);

  // Get global location filter from Redux
  const globalLocationFilter = useSelector((state) => state.selectedLocationFilter);

  // Super Admin org filtering support
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const superAdminSelectedSubOrgId = superAdminSelectedOrgData?.type === 'sub_org' ? superAdminSelectedOrgData?.sub_org_id : null;
  const loginResponse = useSelector((state) => state.loginResponse);
  const isSuperAdmin = loginResponse?.role_name?.toUpperCase() === 'SUPER_ADMIN' || loginResponse?.roleName?.toUpperCase() === 'SUPER_ADMIN';
  const isAdmin = loginResponse?.role_name?.toUpperCase() === 'ADMIN' || loginResponse?.roleName?.toUpperCase() === 'ADMIN';

  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [viewListData, setViewListData] = useState([{
    assignment_id: "",
    start_date: null,
    end_date: null,
    work_schedule_name: "",
    work_schedule_id: "",
    status: "ACTIVE",
  }]);
  const [recordCount, setRecordCount] = useState([]);
  const [showWorkAssignmentForm, setShowWorkAssignmentForm] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null); // State to track selected member

  const [workSchedules, setWorkSchedules] = useState(["W1", "W2", "W3"]); // Example work schedules
  const [previousAssignments, setPreviousAssignments] = useState([]);

  const getMonthNames = () => {
    const date = new Date();
    const monthNames = [];
    for (let i = 0; i < 3; i++) {
      monthNames.push(date.toLocaleString("default", { month: "long" }));
      date.setMonth(date.getMonth() + 1);
    }
    return monthNames;
  };

  const [monthNames, setMonthNames] = useState(getMonthNames());

  const handleAddMemberSuccess = () => {
    fetchMembers(); // Refresh member list after a new member is added
    setIsAddMemberOpen(false); // Close the dialog after success
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchMembers();
  }, [currentPage, globalLocationFilter, superAdminSelectedOrgId, superAdminSelectedSubOrgId]);

  useEffect(() => {
    if (members.length > 0) {
      handleLocationClick(members[0]); // Select the first user when members are fetched
      setSelectedMemberId(members[0].user_id); // Set the first user as selected
    }
  }, [members]);

  useEffect(() => {
    const tabElements = document.querySelectorAll(
      'button[data-bs-toggle="tab"]'
    );
    tabElements.forEach((tab) => {
      tab.addEventListener("shown.bs.tab", (event) => {
        tabElements.forEach((t) => t.classList.remove("active"));
        event.target.classList.add("active");
      });
    });
  }, []);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = borderAnimation + customDatePickerStyles;  // Combine both styles
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const fetchMembers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        pageNumber: currentPage,
        pageSize: pageSize
      };

      // Add global location filter if selected
      if (globalLocationFilter) {
        payload.location_id = globalLocationFilter;
      }

      // Add org_id for Super Admin viewing specific organization
      if (isSuperAdmin && superAdminSelectedOrgId) {
        payload.org_id = superAdminSelectedOrgId;
        if (superAdminSelectedSubOrgId) {
          payload.sub_org_id = superAdminSelectedSubOrgId;
        }
      } else if (isAdmin && superAdminSelectedSubOrgId) {
        // Admin drilled into a sub-org via the header dropdown. No org_id
        // override needed/valid for Admin (their own org is implicit via the
        // JWT header) - only sub_org_id.
        payload.sub_org_id = superAdminSelectedSubOrgId;
      }

      const response = await fetchMemberApi(payload); // Fetch from your API

      // Log the entire response to debug
      console.log("API Response:", response);

      // Accessing the data inside the response
      const { businessStatusCode, summaryData, totalPages, totalRecords } = response.data;

      // Check if the response contains the data as expected
      if (businessStatusCode === 2 && summaryData) {
        setMembers(summaryData); // Set the state with the summaryData
        setTotalPages(totalPages || 1);
        setTotalRecords(totalRecords || summaryData.length);
        fetchWorkScheduleForMember(summaryData?.[0]?.org_id); // Fetch work schedules
      } else {
        setMembers([]);
        setTotalPages(1);
        setTotalRecords(0);
        setError("No members found. Please add members to get started.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWorkScheduleForMember = async (id) => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        entity_type: "work_schedule",
        columns: [
            "work_schedule_id",
            "schedule_name"
        ],
        parameter: [
            {
                "columnName": "status",
                "condition": "=",
                "value": "ACTIVE"
            },
            {
                "columnName": "org_id",
                "condition": "=",
                "value": id,
            }   
        ]
    }
      const response = await fetchWorkSchedule(payload); // Fetch from your API
      // Check if the response contains the data as expected
      if (response.businessStatusCode === 2 && response.list) {
        setWorkSchedules(response.list); // Set the state with the list of work schedules
      } else {
        setError("No members found. Please add members to get started.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredMembers = members?.filter(
    (member) =>
      member?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.employee_id?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const handleMarkerClick = (segment) => {
    setSelectedSegment(segment);
  };
  const handleEditBack = () => {
    setIsEditing(false);
    if (recordCount === 0) {
      setShowWorkAssignmentForm(false);
    }
  };

// Add this new state to track original data
const [originalData, setOriginalData] = useState([]);

// Modify the FetchWorkAssignment response handler in handleLocationClick
const handleLocationClick = async (member) => {
  setSelectedMemberId(member.user_id);
  const requestBody = {
    userId: member.user_id,
  };
  // Super Admin: scope the fetch to the org selected in the top-bar filter so a
  // member's Admin-created assignments (in another org) are returned.
  if (isSuperAdmin && superAdminSelectedOrgId) {
    requestBody.org_id = superAdminSelectedOrgId;
  }

  try {
    const response = await FetchWorkAssignment(requestBody);
    const { businessStatusCode, workScheduleAssignment, previousWorkScheduleAssignment } = response;

    // Handle current assignments
    if (workScheduleAssignment && workScheduleAssignment.length > 0) {
      const transformedData = workScheduleAssignment.map(assignment => ({
        assignment_id: assignment.assignment_id,
        start_date: new Date(assignment.start_date),
        end_date: new Date(assignment.end_date),
        work_schedule_name: assignment.work_schedule_id,
        work_schedule_id: assignment.work_schedule_id,
        status: "ACTIVE",
      }));

      setViewListData(transformedData);
      setOriginalData(JSON.parse(JSON.stringify(transformedData))); // Store a deep copy of original data
      setRecordCount(workScheduleAssignment.length);
      setShowWorkAssignmentForm(true);
    } else {
      setViewListData([]);
      setOriginalData([]);
      setRecordCount(0);
      setShowWorkAssignmentForm(false);
      // Show info toast when no work schedule is found
      toast.info(`No work schedule assignments found for ${member.full_name}`);
    }

    // Handle previous assignments
    if (previousWorkScheduleAssignment && previousWorkScheduleAssignment.length > 0) {
      const transformedPreviousData = previousWorkScheduleAssignment.map(assignment => ({
        assignment_id: assignment.assignment_id,
        start_date: new Date(assignment.start_date),
        end_date: new Date(assignment.end_date),
        work_schedule_name: assignment.work_schedule_id,
        work_schedule_id: assignment.work_schedule_id,
      }));
      setPreviousAssignments(transformedPreviousData);
    } else {
      setPreviousAssignments([]);
    }
    
    // Clear any existing error
    setError(null);
    
  } catch (err) {
    // Don't set error for no work schedule, just clear the data
    setViewListData([]);
    setOriginalData([]);
    setRecordCount(0);
    setShowWorkAssignmentForm(false);
    setPreviousAssignments([]);
    console.error("Error fetching work assignments:", err);
    // Show error toast for API errors
    toast.error("Error fetching work assignments");
  }
};

  const insidePremisesSegments = segments.filter(
    (segment) => segment.inside_premises
  );

  const formatTime1 = (time) => {
    const [hour, minute] = time.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12; // Convert 0 or 12 to 12 for 12-hour format
    return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

// Replace the existing handleAddRow function with this updated version
const handleAddRow = (index) => {
  // Get the end date of the current row
  const currentEndDate = viewListData[index].end_date;
  
  // Find the next available date that doesn't overlap with existing assignments
  const nextAvailableDate = findNextAvailableDate(currentEndDate, viewListData);
  
  const newRow = {
    assignment_id: "",
    start_date: nextAvailableDate,
    end_date: nextAvailableDate, // Set end date same as start date initially
    work_schedule_name: "",
  };
  
  const updatedData = [...viewListData];
  updatedData.splice(index + 1, 0, newRow); // Insert new row after the current index
  setViewListData(updatedData);
};

  const [deleteAssignments, setDeleteAssignments] = useState([]); // Rename state variable for clarity
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [rowToRemove, setRowToRemove] = useState(null);

  const handleRemoveRow = (index) => {
    if (viewListData.length === 1) {
      setRowToRemove(index);
      setIsConfirmDialogOpen(true);
    } else {
      removeRow(index);
    }
  };

  const removeRow = (index) => {
    const updatedData = [...viewListData];
    const removedItem = updatedData.splice(index, 1)[0];
    setViewListData(updatedData);
    if (removedItem.assignment_id) {
      // Store complete assignment data for deletion
      setDeleteAssignments([...deleteAssignments, {
        assignmentId: removedItem.assignment_id,
        userId: selectedMemberId,  // Make sure selectedMemberId is being used here
        workScheduleId: removedItem.work_schedule_id,
        workScheduleName: workSchedules.find(
          schedule => schedule.work_schedule_id === removedItem.work_schedule_id
        )?.schedule_name || "",
        startDate: removedItem.start_date,
        endDate: removedItem.end_date
      }]);
    }
    if (updatedData.length === 0) {
      setRecordCount(0);
      setShowWorkAssignmentForm(false);
    }
  };

const handleConfirmRemove = async () => {
  if (rowToRemove !== null) {
    const removedItem = viewListData[rowToRemove];
    const deleteAssignment = {
      assignmentId: removedItem.assignment_id,
      userId: selectedMemberId,  // Make sure selectedMemberId is being used here
      workScheduleId: removedItem.work_schedule_id,
      workScheduleName: workSchedules.find(
        schedule => schedule.work_schedule_id === removedItem.work_schedule_id
      )?.schedule_name || "",
      startDate: removedItem.start_date,
      endDate: removedItem.end_date
    };

    console.log('Delete payload:', { deleteAssignments: [deleteAssignment] }); // Add this for debugging

    try {
      const response = await saveWorkAssignmentsApi({
        scheduleAssignments: [], 
        deleteAssignments: [deleteAssignment]
      });
      
      if (response.businessStatusCode === 2) {
        removeRow(rowToRemove);
        setRowToRemove(null);
        setIsConfirmDialogOpen(false);
        toast.success(response.message);
      } else {
        throw new Error("Failed to remove work assignment");
      }
    } catch (err) {
      setError(err.message);
      setRowToRemove(null);
      setIsConfirmDialogOpen(false);
    }
  }
};

  const handleCancelRemove = () => {
    setRowToRemove(null);
    setIsConfirmDialogOpen(false);
  };

  const handleInputChange = (index, field, value) => {
    const updatedData = [...viewListData];
    updatedData[index][field] = value;
    
    // If changing work schedule, update both name and id
    if (field === 'work_schedule_name') {
      updatedData[index].work_schedule_id = value;
    }
    
    setViewListData(updatedData);
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set time to midnight to compare only dates
    return date < today;
  };

  const isPastOrCurrentDateMinusOne = (date) => {
    const today = new Date();
    today.setDate(today.getDate() - 1); // Set to current date minus one
    today.setHours(0, 0, 0, 0); // Set time to midnight to compare only dates
    return date < today;
  };

  const formatDate = (date) => {
    return date ? format(new Date(date), "dd/MM/yyyy") : "";
  };

  const [isEditing, setIsEditing] = useState(false); // State to manage edit mode
  const [activeTab, setActiveTab] = useState('current'); // Add this state

  // Add this handler function
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setIsEditing(false);
  };

// Modify handleSave function
const handleSave = async () => {
  // Filter out only new and modified assignments
  const scheduleAssignments = viewListData.map(item => {
    // For new assignments
    if (!item.assignment_id) {
      return {
        assignmentId: undefined,
        workScheduleId: item.work_schedule_name, // Use work_schedule_name directly
        workScheduleName: workSchedules.find(
          schedule => schedule.work_schedule_id === item.work_schedule_name
        )?.schedule_name || "",
        startDate: item.start_date,
        endDate: item.end_date,
        userId: selectedMemberId
      };
    }

    // For existing assignments, compare with original data
    const originalItem = originalData.find(orig => orig.assignment_id === item.assignment_id);
    
    // If the item has been modified
    if (!originalItem || 
        new Date(item.start_date).getTime() !== new Date(originalItem.start_date).getTime() ||
        new Date(item.end_date).getTime() !== new Date(originalItem.end_date).getTime() ||
        item.work_schedule_name !== originalItem.work_schedule_name) { // Compare work_schedule_name directly
      return {
        assignmentId: item.assignment_id,
        workScheduleId: item.work_schedule_name, // Use work_schedule_name directly
        workScheduleName: workSchedules.find(
          schedule => schedule.work_schedule_id === item.work_schedule_name
        )?.schedule_name || "",
        startDate: item.start_date,
        endDate: item.end_date,
        userId: selectedMemberId
      };
    }
    
    return null;
  }).filter(item => item !== null); // Remove null entries

  // Determine action based on whether there are assignments with assignmentId
  const hasExistingAssignments = scheduleAssignments.some(item => item.assignmentId);
  const action = hasExistingAssignments ? 'UPDATE' : 'CREATE';

  console.log('Payload scheduleAssignments:', scheduleAssignments); // Add this for debugging

  const payload = {
    action,
    scheduleAssignments,
    deleteAssignments: deleteAssignments
  };

  try {
    const response = await saveWorkAssignmentsApi(payload);
    if (response.businessStatusCode === 2) {
      toast.success(response.message);
      setIsEditing(false);
      setDeleteAssignments([]);
      // Update originalData with current viewListData after successful save
      setOriginalData(JSON.parse(JSON.stringify(viewListData)));
      // Refresh the data
      handleLocationClick({ user_id: selectedMemberId });
    } else if (response.businessStatusCode === 1) {
      toast.error(response.message);
    } else {
      throw new Error("Failed to save work assignments");
    }
  } catch (err) {
    setError(err.message);
  }
};

  const handleCreateAssignment = () => {
    setShowWorkAssignmentForm(true);
    setIsEditing(true);
    setViewListData([{
      assignment_id: "",
      start_date: new Date(),
      end_date: new Date(),
      work_schedule_name: "",
    }]); 
  };

  // Helper to check if any assignment has changed
  const isAssignmentChanged = () => {
    if (viewListData.length !== originalData.length) return true;
    for (let i = 0; i < viewListData.length; i++) {
      const v = viewListData[i];
      const o = originalData[i];
      if (!o ||
        new Date(v.start_date).getTime() !== new Date(o.start_date).getTime() ||
        new Date(v.end_date).getTime() !== new Date(o.end_date).getTime() ||
        v.work_schedule_name !== o.work_schedule_name
      ) {
        return true;
      }
    }
    return false;
  };

  // Save is disabled if any required field is missing or if no changes
  const isSaveDisabled =
    viewListData.some(item => !item.start_date || !item.end_date || !item.work_schedule_name)
    || !isAssignmentChanged();

  // Add this new helper function near your other utility functions
const getMinEndDate = (startDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (isPastDate(new Date(startDate))) {
    const yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    yesterdayDate.setHours(0, 0, 0, 0);
    return yesterdayDate;
  }

  return new Date(startDate);
};

  // Show "Select Organization" message if Super Admin hasn't selected an org
  if (isSuperAdmin && !superAdminSelectedOrgId) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen" style={{ backgroundColor: "#EFF0F4" }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Work Assignment</h1>
            <p className="text-sm text-gray-1000">Super Admin View</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow-sm p-12 mt-8">
          <svg
            className="w-24 h-24 text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Select an Organization</h2>
          <p className="text-gray-1000 text-center max-w-md">
            Please select an organization from the dropdown in the header to view and manage work assignments.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0 m-0">
      <div className="row" style={{ backgroundColor: "#EFF0F4", height: "100%" }}>
        <div className="col-12 align-items-center mt-3  border-bottom text-black-50">
          <div className="relative w-64">
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-1000 focus:outline-none focus:border-gray-300 focus:ring-0 mb-3 shadow"
              placeholder="Search User"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Members Table */}
        <div className="col-12 col-md-3 text-center border-end text-black-50 bg-white shadow card" style={{ height: "76vh", display: 'flex', flexDirection: 'column' }}>
          {error ? (
            <div className="col-12 text-center mt-2 text-red-1000">{error}</div>
          ) : (
            <div className="d-flex flex-column h-100">
              {/* Table Header */}
              <p className="mt-2 pb-2 border-bottom text-black-50 fw-bold w-full items-center" style={{ flexShrink: 0 }}>
                Users List
              </p>

              {/* Table Body - Scrollable */}
              <div style={{ 
                flex: '1 1 auto',
                overflowY: 'auto',
                paddingRight: '5px',
                scrollbarWidth:'none',
                msOverflowStyle:'none',
              }}>
                {filteredMembers.map((member) => (
                  <div
                    key={member.user_id}
                    className={`items-center px-1 py-1 shadow-sm`}
                    style={{
                      padding: "15px",
                      backgroundColor: "#ffffff",
                      color: selectedMemberId === member.user_id ? "#FF8800" : "#333",
                      marginBottom: "1rem",
                      border: selectedMemberId === member.user_id ? "2px solid #FF8800" : "1px solid #eeeeee",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      borderRadius: "20px"
                    }}
                    onClick={() => handleLocationClick(member)}
                  >
                    <div className="items-center">
                      <div className="ml-3 flex items-center">
                        <UserAvatar member={member} />
                        <div className="ml-3">
                          <div className={`text-sm font-medium ${
                            selectedMemberId === member.user_id ? 'text-[#FF8800]' : 'text-gray-900'
                          }`}>
                            {member.full_name && member.full_name.trim() !== "" && member.full_name !== "null"
                              ? member.full_name
                              : member.email || member.user_id}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls - Fixed at bottom */}
              {totalPages > 1 && (
                <div className="d-flex flex-column align-items-center py-2 px-2 border-top" style={{ flexShrink: 0, backgroundColor: '#fff', gap: '4px' }}>
                  <div style={{ fontSize: '11px', color: '#888', fontWeight: 500 }}>
                    {currentPage} / {totalPages}
                  </div>
                  <nav>
                    <ul className="pagination pagination-sm mb-0" style={{ gap: '2px' }}>
                      <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
                        <button className="page-link" style={{ fontSize: '12px', padding: '3px 7px' }} onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</button>
                      </li>
                      <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
                        <button className="page-link" style={{ fontSize: '12px', padding: '3px 7px' }} onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                      </li>
                      {(() => {
                        const pages = [];
                        const delta = 2;
                        const left = currentPage - delta;
                        const right = currentPage + delta;
                        let lastPage = null;
                        for (let i = 1; i <= totalPages; i++) {
                          if (i === 1 || i === totalPages || (i >= left && i <= right)) {
                            if (lastPage && i - lastPage > 1) {
                              pages.push(
                                <li key={`ellipsis-${i}`} className="page-item disabled">
                                  <span className="page-link" style={{ fontSize: '11px', padding: '3px 6px' }}>...</span>
                                </li>
                              );
                            }
                            pages.push(
                              <li key={i} className={`page-item${currentPage === i ? ' active' : ''}`}>
                                <button
                                  className="page-link"
                                  style={{
                                    fontSize: '11px', padding: '3px 7px',
                                    backgroundColor: currentPage === i ? '#FF8800' : '',
                                    borderColor: currentPage === i ? '#FF8800' : '',
                                    color: currentPage === i ? '#fff' : '',
                                  }}
                                  onClick={() => setCurrentPage(i)}
                                >{i}</button>
                              </li>
                            );
                            lastPage = i;
                          }
                        }
                        return pages;
                      })()}
                      <li className={`page-item${currentPage === totalPages ? ' disabled' : ''}`}>
                        <button className="page-link" style={{ fontSize: '12px', padding: '3px 7px' }} onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
                      </li>
                      <li className={`page-item${currentPage === totalPages ? ' disabled' : ''}`}>
                        <button className="page-link" style={{ fontSize: '12px', padding: '3px 7px' }} onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>»</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="col-12 col-md-9 shadow-sm" style={{ height: "76vh" }}>
          {recordCount > 0 || showWorkAssignmentForm ? (
            <div className="col-12 col-md-12 px-4 bg-white py-2 card " style={{ maxHeight: "100%", overflowY: "auto" }}>
              {/* Horizontal Tabs Navigation */}
              <ul
                className="row nav nav-tabs bg-white text-center p-0 m-0"
                role="tablist"
              >
                <div className="col-12 d-flex">
                  <li
                    className="col-4 nav-item rounded"
                    role="presentation"
                    onClick={() => handleTabChange('current')}
                    style={{ cursor: 'pointer' }}
                  >
                    <button
                      className={`nav-link ${activeTab === 'current' ? 'active' : ''} w-100 h-100`}
                      id="tab1-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab1"
                      type="button"
                      role="tab"
                      aria-controls="tab1"
                      aria-selected={activeTab === 'current'}
                      style={{ border: "none" }}
                    >
                      Current Assignments
                    </button>
                  </li>
                  <li
                    className="col-4 nav-item rounded"
                    role="presentation"
                    onClick={() => handleTabChange('previous')}
                    style={{ cursor: 'pointer' }}
                  >
                    <button
                      className={`nav-link ${activeTab === 'previous' ? 'active' : ''} w-100 h-100`}
                      id="tab2-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab2"
                      type="button"
                      role="tab"
                      aria-controls="tab2"
                      aria-selected={activeTab === 'previous'}
                      style={{
                        border: "none",
                        borderBottom: "3px solid transparent",
                      }}
                    >
                      Previous Assignments
                    </button>
                  </li>
                </div>
              </ul>
              <div className="text-black-50 d-flex justify-content-end py-1">
                {!isEditing && activeTab === 'current' && (
                  <Pencil
                    size={25}
                    onClick={isSuspended ? undefined : () => setIsEditing(true)}
                    title={isSuspended ? "Actions are disabled - organization is suspended" : "Edit"}
                    style={{
                      cursor: isSuspended ? "not-allowed" : "pointer",
                      backgroundColor: isSuspended ? "#e0e0e0" : "#f0f0f0",
                      padding: "5px",
                      borderRadius: "20px",
                      opacity: isSuspended ? 0.5 : 1,
                    }}
                  />
                )}
              </div>
              {/* Tab Content */}
              <div className="tab-content row border my-1 bg-white rounded mx-1" style={{ borderTop: "none", height: "56vh" }}>
                <div
                  className={`tab-pane fade ${activeTab === 'current' ? 'show active' : ''} h-100`}
                  id="tab1"
                  role="tabpanel"
                  aria-labelledby="tab1-tab"
                  style={{ overflowY: "auto" }}
                >
                  <table className="table">
                    <thead className="sticky-top bg-white" style={{ zIndex: 1 }}>
                      <tr>
                        <th className="fw-bold">Start Date</th>
                        <th className="fw-bold">End Date</th>
                        <th className="fw-bold">Work Schedule</th>
                        <th className="text-center fw-bold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewListData.map((item, index) => (
                        <tr key={index}>
                          <td className="col-2 col-md-2">
                            <div className="position-relative">
                              <DatePicker
                                selected={item.start_date ? new Date(item.start_date) : null}
                                onChange={(date) => {
                                  if (date) {
                                    // Only allow selection if the date is not already taken
                                    if (!isDateDisabled(date, viewListData, index)) {
                                      handleInputChange(index, "start_date", date);
                                      // If end date is before new start date, update end date
                                      if (!item.end_date || new Date(item.end_date) < date) {
                                        handleInputChange(index, "end_date", date);
                                      }
                                    } else {
                                      toast.error("This date is already assigned in another schedule");
                                    }
                                  }
                                }}
                                filterDate={(date) => {
                                  // Disable past dates and already selected dates
                                  const isPast = isPastDate(date);
                                  const isDisabled = isDateDisabled(date, viewListData, index);
                                  return !isPast && !isDisabled;
                                }}
                                placeholderText="Select start date"
                                dateFormat="dd/MM/yyyy"
                                className="form-control text-xs w-100"
                                disabled={!isEditing || isPastDate(new Date(item.start_date))}
                                style={{ opacity: !isEditing || isPastDate(new Date(item.start_date)) ? 0.5 : 1 }}
                                minDate={new Date()} // Disable previous dates
                                portalId="root"
                              />
                            </div>
                          </td>
                          <td className="col-2 col-md-2">
                            <div className="position-relative">
                              <DatePicker
                                selected={item.end_date ? new Date(item.end_date) : null}
                                onChange={(date) => {
                                  if (date) {
                                    const startDate = new Date(item.start_date);
                                    // Check if the date range is available
                                    let isRangeAvailable = true;
                                    let currentDate = new Date(startDate);
                                    while (currentDate <= date) {
                                      if (isDateDisabled(currentDate, viewListData, index)) {
                                        isRangeAvailable = false;
                                        break;
                                      }
                                      currentDate.setDate(currentDate.getDate() + 1);
                                    }
                                  
                                    if (isRangeAvailable) {
                                      handleInputChange(index, "end_date", date);
                                    } else {
                                      toast.error("The selected date range overlaps with existing assignments");
                                    }
                                  }
                                }}
                                filterDate={(date) => {
                                  // Disable dates before start date, past dates, and already selected dates
                                  const minDate = item.start_date ? new Date(item.start_date) : new Date();
                                  const isPast = isPastOrCurrentDateMinusOne(date);
                                  const isBeforeStart = date < minDate;
                                  const isDisabled = isDateDisabled(date, viewListData, index);
                                  return !isPast && !isBeforeStart && !isDisabled;
                                }}
                                placeholderText="Select end date"
                                dateFormat="dd/MM/yyyy"
                                className="form-control text-xs w-100"
                                disabled={!isEditing || !item.start_date || isPastOrCurrentDateMinusOne(new Date(item.end_date))}
                                style={{ opacity: !isEditing || !item.start_date || isPastOrCurrentDateMinusOne(new Date(item.end_date)) ? 0.5 : 1 }}
                                minDate={item.start_date ? new Date(item.start_date) : new Date()}
                                portalId="root"
                              />
                            </div>
                          </td>
                          <td className="col-3 col-md-4">
                            <select
                              className="text-sm w-full p-2" // Change text-sm to text-xs to further reduce font size
                              value={item.work_schedule_name}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "work_schedule_name",
                                  e.target.value
                                )
                              }
                              disabled={!isEditing || isPastDate(new Date(item.end_date))}
                              style={{ opacity: !isEditing || isPastDate(new Date(item.end_date)) ? 0.5 : 1 }}
                            >
                              <option value="" disabled >Select schedule</option>
                              {workSchedules.map((schedule, i) => (
                                <option key={i} value={schedule.work_schedule_id}>
                                  {schedule.schedule_name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="col-3 col-md-2 text-center">
                            {isEditing && (
                              <>
                                <button onClick={() => handleAddRow(index)}>
                                  <PlusCircle size={12} color="black" />{" "}
                                  {/* Set the color to black */}
                                </button>
                                <button onClick={() => handleRemoveRow(index)}
                                 disabled={!isEditing || isPastDate(new Date(item.end_date))}
                              style={{ opacity: !isEditing || isPastDate(new Date(item.end_date)) ? 0.5 : 1 }} className="ms-1"
                              >
                                  <MinusCircle size={12} color="black" />{" "}
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                
                <div
                  className={`tab-pane fade ${activeTab === 'previous' ? 'show active' : ''} h-100`}
                  id="tab2"
                  role="tabpanel"
                  aria-labelledby="tab2-tab"
                  style={{ overflowY: "auto" }}
                >
                  {previousAssignments.length > 0 ? (
                    <table className="table">
                      <thead className="sticky-top bg-white" style={{ zIndex: 1 }}>
                        <tr>
                          <th className="fw-bold">Start Date</th>
                          <th className="fw-bold">End Date</th>
                          <th className="fw-bold">Work Schedule</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previousAssignments.map((item, index) => (
                          <tr key={index}>
                            <td className="col-2 col-md-2">
                              <div className="position-relative">
                                <DatePicker
                                  selected={item.start_date ? new Date(item.start_date) : null}
                                  dateFormat="dd/MM/yyyy"
                                  className="form-control text-xs w-100"
                                  disabled={true}
                                  style={{ opacity: 0.5 }}
                                />
                              </div>
                            </td>
                            <td className="col-2 col-md-2">
                              <div className="position-relative">
                                <DatePicker
                                  selected={item.end_date ? new Date(item.end_date) : null}
                                  dateFormat="dd/MM/yyyy"
                                  className="form-control text-xs w-100"
                                  disabled={true}
                                  style={{ opacity: 0.5 }}
                                />
                              </div>
                            </td>
                            <td className="col-3 col-md-4">
                              <select
                                className="text-sm w-full p-2"
                                value={item.work_schedule_name}
                                disabled={true}
                                style={{ opacity: 0.5 }}
                              >
                                <option value={item.work_schedule_id}>
                                  {workSchedules.find(schedule => schedule.work_schedule_id === item.work_schedule_id)?.schedule_name || item.work_schedule_name}
                                </option>
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="d-flex justify-content-center align-items-center h-100">
                      <p className="text-muted fw-bold text-center">
                        No previous assignments available.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              {isEditing && (
                <div className="d-flex bg-white justify-content-end mt-3">
                  <ArthaCancelButton className="me-2" onClick={handleEditBack}>Cancel</ArthaCancelButton>
                  <ArthaContinueButton
                    className=""
                    onClick={isSuspended ? undefined : handleSave}
                    disabled={isSaveDisabled || isSuspended}
                    style={isSuspended ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                    title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                  >Save</ArthaContinueButton>
                </div>
              )}
            </div>
          ) : (
            <div className="col-12 col-md-12 mx-auto px-5 py-3 mt-4">
              <UserImage
                alt="Image Not Found"
                className="w-50 h-50 mx-auto px-5 py-2"
              />
              <p className="fw-bold text-center mt-3">
                No Work Assignment found for the selected member.
              </p>
              <div className="text-center mt-3">
                <ArthaContinueButton
                  className="w-50 h-10"
                  onClick={isSuspended ? undefined : handleCreateAssignment}
                  disabled={isSuspended}
                  style={isSuspended ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                  title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
                >
                  Create Work Assignment
                </ArthaContinueButton>
              </div>
            </div>
          )}
        </div>

        <Dialog
          open={isAddMemberOpen}
          onClose={() => setIsAddMemberOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <AddMember
            isOpen={isAddMemberOpen}
            onClose={() => setIsAddMemberOpen(false)}
            onSuccess={handleAddMemberSuccess}
          />
        </Dialog>
        <Dialog
          open={isConfirmDialogOpen}
          onClose={handleCancelRemove}
        >
          <DialogTitle>Confirm Removal</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to remove the last work assignment? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <ArthaCancelButton onClick={handleCancelRemove}>Cancel</ArthaCancelButton>
            <ArthaContinueButton onClick={handleConfirmRemove}>Confirm</ArthaContinueButton>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default WorkAssignment;
